// Taktstriche, Wiederholungen (<repeat>) und Volta-Klammern (<ending>) – pure MusicXML-DOM-Logik.
// Abhängigkeitsregel im Ordner: geteilte Basis-Helfer in `core.ts`; gerichtete Geschwister-Imports
// sind erlaubt, ZYKLEN nicht. Dieses Modul hängt nur an `core` (und wird selbst von `structure`
// importiert – Richtung structure → barlines).
//
// Tragende Invarianten (Begründung: docs/webapp/musicxml-dom-edits.md, „Taktstriche & Wiederholungen"):
// - Kind-Reihenfolge im `<barline>`: **bar-style → ending → repeat** (MusicXML-Schema; music21
//   exportiert dieselbe Ordnung – ein Ending-Stop und ein Backward-Repeat teilen sich eine Barline).
// - Die **linke** Barline steht nach `<attributes>`/`<print>` und vor der ersten `<note>`, die
//   **rechte** ist letztes Takt-Kind.
// - Alle Setter wirken über **alle Parts**, adressiert per **Ordinalindex** (nie `number`-Attribut –
//   gerade bei Wiederholungsstrukturen kommen doppelte/0-Nummern vor).
// - `setEndingInMusicXml` garantiert **balancierte, überlappungsfreie** start/stop-Paare: music21
//   hängt beim Parsen jedes `<ending>` an das erste unvollständige RepeatBracket und verschmilzt
//   unbalancierte Klammern unvorhersehbar; der Speichern-Pfad der Webapp ist ein music21-Roundtrip.
import {findMeasureInPartByIndex, getParts, parseMusicXml, serializeMusicXml} from './core';

export type BarlineSide = 'left' | 'right';

/** Auswahl der UI: Normal / Doppelstrich / Schlussstrich. `regular` wird nicht geschrieben,
 *  sondern entfernt ein explizites `<bar-style>` (Renderer-Default ist ohnehin regular). */
export type BarStyleChoice = 'regular' | 'light-light' | 'light-heavy';

export interface EndingSpan {
    number: number;
    startIndex: number;
    endIndex: number;
}

export interface MeasureStructureInfo {
    measureIndex: number;
    measureCount: number;
    /** Linke Barline trägt `<repeat direction="forward"/>`. */
    repeatStart: boolean;
    /** Rechte Barline trägt `<repeat direction="backward"/>`. */
    repeatEnd: boolean;
    /** `times`-Attribut des Backward-Repeats (`null` = nicht gesetzt = Default 2 Durchgänge). */
    repeatTimes: number | null;
    barStyle: BarStyleChoice;
    /** Span, dem dieser Takt angehört (start/innerhalb/stop), sonst `null`. */
    ending: EndingSpan | null;
    /** Alle Spans des Stücks (für Überlappungs-Hinweise in der UI). */
    endings: EndingSpan[];
}

// ============ interne Helfer ============

const BARLINE_CHILD_ORDER: Record<string, number> = {'bar-style': 0, 'ending': 1, 'repeat': 2};

function measuresOf(part: Element): Element[] {
    return [...part.querySelectorAll('measure')];
}

/** Barline einer Seite; ein `<barline>` ohne `location` gilt laut Schema als `right`. */
function barlineOf(measure: Element, side: BarlineSide): Element | null {
    for (const bl of measure.querySelectorAll(':scope > barline')) {
        if ((bl.getAttribute('location') ?? 'right') === side) return bl;
    }
    return null;
}

/** Holt oder erzeugt die Barline einer Seite an ihrer schema-korrekten Position. */
function ensureBarline(doc: Document, measure: Element, side: BarlineSide): Element {
    const existing = barlineOf(measure, side);
    if (existing) return existing;
    const bl = doc.createElement('barline');
    bl.setAttribute('location', side);
    if (side === 'right') {
        measure.appendChild(bl);
    } else {
        // Links: vor der ersten Note (damit automatisch NACH <attributes>/<print>); ohne Noten
        // vor einer vorhandenen rechten Barline, sonst ans Ende.
        const firstNote = measure.querySelector(':scope > note');
        const rightBl = barlineOf(measure, 'right');
        measure.insertBefore(bl, firstNote ?? rightBl);
    }
    return bl;
}

/** Fügt `el` in kanonischer Kindreihenfolge ein; ein vorhandenes Kind gleichen Tags wird ersetzt. */
function setBarlineChild(barline: Element, el: Element): void {
    const tag = el.tagName.toLowerCase();
    const order = BARLINE_CHILD_ORDER[tag] ?? 99;
    for (const child of [...barline.children]) {
        if (child.tagName.toLowerCase() === tag) child.remove();
    }
    let ref: Element | null = null;
    for (const child of [...barline.children]) {
        if ((BARLINE_CHILD_ORDER[child.tagName.toLowerCase()] ?? 99) > order) {
            ref = child;
            break;
        }
    }
    barline.insertBefore(el, ref);
}

/** Entfernt eine leer gewordene `<barline>` (kein Element-Kind mehr) ganz. */
function pruneBarline(barline: Element | null): void {
    if (barline && !barline.children.length) barline.remove();
}

/** Wendet `fn` auf den Takt am Ordinalindex in JEDEM Part an. `false`, wenn Part 0 den Takt
 *  nicht hat (ungültiger Index); Parts ohne diesen Takt werden übersprungen (Parts sind normal
 *  ausgerichtet – Defensive für degenerierte Inputs). */
function forEachPartMeasure(doc: Document, measureIndex: number, fn: (measure: Element) => void): boolean {
    const parts = getParts(doc);
    const first = parts[0];
    if (!first || !findMeasureInPartByIndex(first, measureIndex)) return false;
    for (const part of parts) {
        const measure = findMeasureInPartByIndex(part, measureIndex);
        if (measure) fn(measure);
    }
    return true;
}

function endingSpansOfPart(part: Element): EndingSpan[] {
    const spans: EndingSpan[] = [];
    const measures = measuresOf(part);
    let open: { number: number; startIndex: number } | null = null;
    measures.forEach((m, i) => {
        const start = barlineOf(m, 'left')?.querySelector(':scope > ending[type="start"]');
        if (start) {
            // Neuer Start bei offenem Span (unbalancierter Input) → alten defensiv davor schließen.
            if (open) spans.push({number: open.number, startIndex: open.startIndex, endIndex: Math.max(open.startIndex, i - 1)});
            open = {number: parseInt(start.getAttribute('number') || '1', 10) || 1, startIndex: i};
        }
        const stop = barlineOf(m, 'right')
            ?.querySelector(':scope > ending[type="stop"], :scope > ending[type="discontinue"]');
        if (stop && open) {
            spans.push({number: open.number, startIndex: open.startIndex, endIndex: i});
            open = null;
        }
    });
    if (open !== null) {
        const o = open as { number: number; startIndex: number };
        spans.push({number: o.number, startIndex: o.startIndex, endIndex: measures.length - 1});
    }
    return spans;
}

/** Entfernt in einem Part alle `<ending>`-Elemente der Takte `[from..to]` (beide Barlines). */
function removeEndingsInRange(part: Element, from: number, to: number): void {
    const measures = measuresOf(part);
    for (let i = from; i <= to && i < measures.length; i++) {
        for (const bl of [...measures[i]!.querySelectorAll(':scope > barline')]) {
            for (const ending of [...bl.querySelectorAll(':scope > ending')]) ending.remove();
            pruneBarline(bl);
        }
    }
}

// ============ Lesen ============

/** Ending-Spans des Stücks, gelesen aus **Part 0** (die Schreib-Helfer spiegeln über alle Parts;
 *  ein Part als Quelle vermeidet Mehrdeutigkeit). Ein unbalancierter Start wird defensiv am
 *  letzten Takt geschlossen (music21-Toleranz gespiegelt). */
export function getEndingsInMusicXml(musicXml: string): EndingSpan[] {
    try {
        const part = getParts(parseMusicXml(musicXml))[0];
        return part ? endingSpansOfPart(part) : [];
    } catch (e) {
        console.error('Error reading endings from MusicXML:', e);
        return [];
    }
}

/** Read-Model des Struktur-Editors: Wiederholungen/Volta/Taktstrich-Stil eines Takts, gelesen aus
 *  dem XML (eine Quelle – nicht aus OSMDs RepetitionInstructions). */
export function getMeasureStructureInfo(musicXml: string, measureIndex: number): MeasureStructureInfo | null {
    try {
        const part = getParts(parseMusicXml(musicXml))[0];
        if (!part) return null;
        const measures = measuresOf(part);
        const measure = Number.isInteger(measureIndex) && measureIndex >= 0 ? measures[measureIndex] : undefined;
        if (!measure) return null;

        const left = barlineOf(measure, 'left');
        const right = barlineOf(measure, 'right');
        const backward = right?.querySelector(':scope > repeat[direction="backward"]') ?? null;
        const times = backward?.getAttribute('times');
        const styleText = right?.querySelector(':scope > bar-style')?.textContent ?? '';
        const endings = endingSpansOfPart(part);

        return {
            measureIndex,
            measureCount: measures.length,
            repeatStart: !!left?.querySelector(':scope > repeat[direction="forward"]'),
            repeatEnd: !!backward,
            repeatTimes: times ? parseInt(times, 10) || null : null,
            barStyle: styleText === 'light-light' || styleText === 'light-heavy' ? styleText : 'regular',
            ending: endings.find(s => measureIndex >= s.startIndex && measureIndex <= s.endIndex) ?? null,
            endings,
        };
    } catch (e) {
        console.error('Error reading measure structure from MusicXML:', e);
        return null;
    }
}

// ============ Schreiben ============

/** Setzt/entfernt eine Wiederholung: `start` = `<repeat direction="forward"/>` in der linken,
 *  `end` = `<repeat direction="backward"/>` (+ optional `times` ≥ 2) in der rechten Barline.
 *  Entfernen löscht NUR das `<repeat>` – ein `<ending>` auf derselben Barline bleibt. */
export function setRepeatInMusicXml(
    musicXml: string, measureIndex: number, side: 'start' | 'end', enabled: boolean, times?: number,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const blSide: BarlineSide = side === 'start' ? 'left' : 'right';
        const ok = forEachPartMeasure(doc, measureIndex, (measure) => {
            if (enabled) {
                const repeat = doc.createElement('repeat');
                repeat.setAttribute('direction', side === 'start' ? 'forward' : 'backward');
                if (side === 'end' && times !== undefined && times >= 2) repeat.setAttribute('times', String(times));
                setBarlineChild(ensureBarline(doc, measure, blSide), repeat);
            } else {
                const bl = barlineOf(measure, blSide);
                bl?.querySelector(':scope > repeat')?.remove();
                pruneBarline(bl);
            }
        });
        return ok ? serializeMusicXml(doc) : null;
    } catch (e) {
        console.error('Error setting repeat in MusicXML:', e);
        return null;
    }
}

/** Setzt/entfernt das `times`-Attribut eines VORHANDENEN Backward-Repeats; ohne Repeat → `null`. */
export function setRepeatTimesInMusicXml(musicXml: string, measureIndex: number, times: number | null): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const first = getParts(doc)[0];
        const firstMeasure = first ? findMeasureInPartByIndex(first, measureIndex) : null;
        if (!firstMeasure || !barlineOf(firstMeasure, 'right')?.querySelector(':scope > repeat[direction="backward"]')) {
            return null;
        }
        forEachPartMeasure(doc, measureIndex, (measure) => {
            const repeat = barlineOf(measure, 'right')?.querySelector(':scope > repeat[direction="backward"]');
            if (!repeat) return;
            if (times !== null && times >= 2) repeat.setAttribute('times', String(times));
            else repeat.removeAttribute('times');
        });
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error setting repeat times in MusicXML:', e);
        return null;
    }
}

/** Setzt den Stil der RECHTEN Barline. `regular` entfernt ein explizites `<bar-style>` (Sibling
 *  `<ending>`/`<repeat>` bleiben; leere Barline wird entfernt). */
export function setBarStyleInMusicXml(musicXml: string, measureIndex: number, style: BarStyleChoice): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const ok = forEachPartMeasure(doc, measureIndex, (measure) => {
            if (style === 'regular') {
                const bl = barlineOf(measure, 'right');
                bl?.querySelector(':scope > bar-style')?.remove();
                pruneBarline(bl);
            } else {
                const styleEl = doc.createElement('bar-style');
                styleEl.textContent = style;
                setBarlineChild(ensureBarline(doc, measure, 'right'), styleEl);
            }
        });
        return ok ? serializeMusicXml(doc) : null;
    } catch (e) {
        console.error('Error setting bar style in MusicXML:', e);
        return null;
    }
}

/** Setzt eine Volta-Klammer über `[startIndex..endIndex]`: entfernt zuerst JEDEN überlappenden
 *  Span (strukturelle Nicht-Überlappungs-Garantie), schreibt dann `type="start"` in die linke
 *  Barline des ersten und `type="stop"` in die rechte des letzten Takts – nie `discontinue`
 *  (music21 exportiert es ohnehin als `stop`). */
export function setEndingInMusicXml(
    musicXml: string, startIndex: number, endIndex: number, endingNumber: number,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const parts = getParts(doc);
        const first = parts[0];
        if (!first) return null;
        const count = measuresOf(first).length;
        if (!Number.isInteger(startIndex) || !Number.isInteger(endIndex)
            || startIndex < 0 || endIndex < startIndex || endIndex >= count) {
            return null;
        }

        const overlapping = endingSpansOfPart(first)
            .filter(s => s.startIndex <= endIndex && s.endIndex >= startIndex);
        for (const part of parts) {
            for (const span of overlapping) removeEndingsInRange(part, span.startIndex, span.endIndex);
            // Defensive: auch Streu-Endings im Zielbereich räumen, bevor das neue Paar entsteht.
            removeEndingsInRange(part, startIndex, endIndex);

            const measures = measuresOf(part);
            const startMeasure = measures[startIndex];
            const stopMeasure = measures[endIndex];
            if (!startMeasure || !stopMeasure) continue;
            const start = doc.createElement('ending');
            start.setAttribute('number', String(endingNumber));
            start.setAttribute('type', 'start');
            setBarlineChild(ensureBarline(doc, startMeasure, 'left'), start);
            const stop = doc.createElement('ending');
            stop.setAttribute('number', String(endingNumber));
            stop.setAttribute('type', 'stop');
            setBarlineChild(ensureBarline(doc, stopMeasure, 'right'), stop);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error setting ending in MusicXML:', e);
        return null;
    }
}

/** Entfernt den GANZEN Span, dem der Takt angehört (start UND stop, über alle Parts);
 *  `null`, wenn der Takt zu keiner Klammer gehört. */
export function removeEndingInMusicXml(musicXml: string, measureIndex: number): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const parts = getParts(doc);
        const first = parts[0];
        if (!first) return null;
        const span = endingSpansOfPart(first)
            .find(s => measureIndex >= s.startIndex && measureIndex <= s.endIndex);
        if (!span) return null;
        for (const part of parts) removeEndingsInRange(part, span.startIndex, span.endIndex);
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error removing ending in MusicXML:', e);
        return null;
    }
}

/** Repariert verwaiste Marker in-place (Konsument: `deleteMeasuresInMusicXml` nach dem Löschen):
 *  - `<ending type="start">` ohne späteren Stop und Stops ohne offenen Start werden entfernt
 *    (music21 verschmilzt verwaiste Klammern beim Parsen unvorhersehbar);
 *  - ein `<repeat direction="forward"/>`, auf das KEIN Backward mehr folgt, wird entfernt
 *    (genau die Form, gegen die sich `write_midi_safely` verteidigen muss);
 *  - ein einzelnes Backward-Repeat bleibt (legal: Wiederholung ab Stückanfang). */
export function sanitizeEndingsInDoc(doc: Document): void {
    for (const part of getParts(doc)) {
        const measures = measuresOf(part);

        // Endings balancieren: Dokumentlauf mit offenem Start; Verwaiste sofort entfernen.
        let openStart: Element | null = null;
        for (const m of measures) {
            const left = barlineOf(m, 'left');
            const start = left?.querySelector(':scope > ending[type="start"]') ?? null;
            if (start) {
                if (openStart) {
                    // Zwei offene Starts hintereinander → der frühere ist verwaist.
                    const bl = openStart.parentElement;
                    openStart.remove();
                    pruneBarline(bl);
                }
                openStart = start;
            }
            const right = barlineOf(m, 'right');
            const stop = right?.querySelector(':scope > ending[type="stop"], :scope > ending[type="discontinue"]') ?? null;
            if (stop) {
                if (openStart) openStart = null;
                else {
                    stop.remove();
                    pruneBarline(right);
                }
            }
        }
        if (openStart) {
            const bl = openStart.parentElement;
            openStart.remove();
            pruneBarline(bl);
        }

        // Forward-Repeats ohne folgendes Backward entfernen.
        const forwardAt: { index: number; el: Element }[] = [];
        const backwardIndices: number[] = [];
        measures.forEach((m, i) => {
            const fwd = barlineOf(m, 'left')?.querySelector(':scope > repeat[direction="forward"]');
            if (fwd) forwardAt.push({index: i, el: fwd});
            if (barlineOf(m, 'right')?.querySelector(':scope > repeat[direction="backward"]')) backwardIndices.push(i);
        });
        for (const {index, el} of forwardAt) {
            if (!backwardIndices.some(b => b >= index)) {
                const bl = el.parentElement;
                el.remove();
                pruneBarline(bl);
            }
        }
    }
}
